import { Link } from 'react-router-dom';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import { FaCheck } from './icons';

type Step = 1 | 2 | 3;

const STEPS: { n: Step; label: string; hint: string; to: string; roles?: string[] }[] = [
  { n: 1, label: 'Classes', hint: 'Grades, sections, subjects', to: '/school-setup/class' },
  { n: 2, label: 'Chapters', hint: 'Units per subject', to: '/school-setup/chapters' },
  // Same roles as the RoleGate on the Teachers route (superuser always passes).
  { n: 3, label: 'Teachers', hint: 'Who teaches what', to: '/school-setup/teachers', roles: ['school_admin', 'school', 'admin', 'superuser'] },
];

const canSee = (s: (typeof STEPS)[number], role?: string | null) => !s.roles || (!!role && s.roles.includes(role));

/** How many steps this role sees (2 when the Teachers step is hidden). */
export const setupStepTotal = (role?: string | null) => STEPS.filter((s) => canSee(s, role)).length;

/**
 * The three-step Setup navigation (Classes, Chapters, Teachers). Reads the
 * role itself, so the Teachers step is hidden for roles the route would block.
 */
export default function SetupSteps({ current }: { current: Step }) {
  const { role } = useAuth();
  const visible = STEPS.filter((s) => canSee(s, role));
  return (
    <nav aria-label="Setup steps" className="mb-[22px] flex flex-wrap gap-2.5">
      {visible.map((s) => {
        const on = s.n === current;
        const done = s.n < current;
        return (
          <Link
            key={s.n}
            to={s.to}
            aria-current={on ? 'step' : undefined}
            className={`flex min-h-[64px] min-w-0 flex-[1_1_200px] items-center gap-3 rounded-[16px] border bg-surface px-3.5 py-3 text-ink transition-colors hover:border-line-strong ${
              on ? 'border-navy shadow-[0_0_0_1px_theme(colors.navy)] dark:border-mist-fixed dark:shadow-[0_0_0_1px_theme(colors.mist-fixed)]' : 'border-line'
            }`}>
            <span
              className={`grid h-8 w-8 flex-none place-items-center rounded-full font-mono text-[13px] font-bold ${
                on ? 'bg-accent text-navy' : done ? 'bg-success-bg text-success' : 'bg-track text-muted'
              }`}>
              {done ? <FaCheck size={14} aria-hidden /> : s.n}
            </span>
            <span className="min-w-0">
              <span className="block font-semibold">{s.label}</span>
              <span className="block truncate text-xs text-muted">{s.hint}</span>
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
